import React, { Fragment, useState } from 'react';
import styles from './styles.module.scss';
import Typography from '@components/common/typography';
import { useNavigate } from 'react-router-dom';
import Tabs from '@components/tabs';
import Button from '@components/common/button';
import Input from '@components/common/input';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiAdminService } from '@api';
import { useForm } from '@hooks/helpers/useForm';
import FormField from '@components/form_field';
import { FormProvider } from 'react-hook-form';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { ELoyaltyLevel, TLoyaltySettings } from '@xeppt/xeppt-sdk/types/loyalty';
import { toast } from 'react-toastify';
import { useLocales } from '@hooks/helpers/useLocales';
import { convertStringsToNumbers } from '@utils/index';
import { EFundsOperation } from '@enum';
import { EUserType } from '@xeppt/xeppt-sdk/types/user';

const paymentMethods = [
    { key: EFundsOperation.WALLET_CREDIT_TRANSFER, label: 'Send money via XEPPT' },
    { key: EFundsOperation.WALLET_DEBIT_TRANSFER, label: 'Receive money via XEPPT' },
    { key: EFundsOperation.WALLET_CREDIT_REQUEST, label: 'Approve someone`s money request' },
    { key: EFundsOperation.WALLET_DEBIT_REQUEST, label: 'Request money via XEPPT (when approved)' },
    { key: EFundsOperation.WALLET_CREDIT_CARD_DEPOSIT, label: 'Load XEPPT card balance' },
    // { key: EFundsOperation.WALLET_CREDIT_CARD_SERVICE, label: 'Order XEPPT card' },
    { key: EFundsOperation.PAY_BILL_CREDIT_PAYMENT, label: 'Pay a bill' },
    { key: EFundsOperation.E_TRANSFER_CREDIT_TRANSFER, label: 'Send money via Interac e-Transfer' },
    {
        key: EFundsOperation.E_TRANSFER_DEBIT_TRANSFER,
        label: 'Receive money via Interac e-Transfer'
    },
    {
        key: EFundsOperation.E_TRANSFER_DEBIT_REQUEST,
        label: 'Request money via Interac e-Transfer (when approved)'
    },
    { key: EFundsOperation.EFT_CREDIT_WITHDRAWAL, label: 'Withdraw to linked bank account' },
    { key: EFundsOperation.EFT_DEBIT_DEPOSIT, label: 'Load balance from linked bank account' },
    { key: EFundsOperation.EPS_DEBIT_DEPOSIT, label: 'Load balance from linked card' },
    { key: EFundsOperation.CARD_CREDIT_WITHDRAWAL, label: 'XEPPT card transactions' },
    { key: EFundsOperation.CARD_DEBIT_DEPOSIT, label: 'XEPPT card load balance' },
    { key: EFundsOperation.GIFT_CARD_DEBIT_GIFT_CARD_REDEEM, label: 'Redeem XEPPT gift card' },
    { key: EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD, label: 'Issue XEPPT gift card' }
];
// Helper function to get only numeric values from enum
const getEnumNumericValues = (enumObject: any): number[] => {
    return Object.values(enumObject).filter((value) => typeof value === 'number') as number[];
};

const LoyaltyLayout = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<EUserType>(EUserType.INDIVIDUAL);
    const [activeTabFeePont, setActiveTabFeePont] = useState<'fee' | 'point'>('point');
    const [activeLevel, setActiveLevel] = useState<ELoyaltyLevel>(ELoyaltyLevel.A);
    const { requestErrorLocale } = useLocales();
    const [isEdit, setIsEdit] = useState(false);
    const form = useForm({});

    const { data: loyalty, handleRequest: refetchLoyalty } = useApiQuery({
        method: () => apiAdminService.getLoyaltySettings(),
        onSuccess: (data) => {
            form.reset(data);
        }
    });

    const { handleRequest: handleUpdateSettings, isLoading } = useApiMutation({
        method: () =>
            apiAdminService.updateLoyaltySettings(
                convertStringsToNumbers(form.getValues()) as TLoyaltySettings
            ),
        onSuccess: () => {
            setIsEdit(false);
            toast.success('Settings were successfully updated');
            refetchLoyalty();
        },
        onError: requestErrorLocale
    });

    // Get only numeric values from enums
    const userTypes = getEnumNumericValues(EUserType) as EUserType[];
    const loyaltyLevels = getEnumNumericValues(ELoyaltyLevel) as ELoyaltyLevel[];
    const feePointTypes = ['fee', 'point'] as const;

    const renderFormField = (fieldName: string, inputProps: any = {}) => (
        <FormField
            name={fieldName}
            renderComponent={(props) => (
                <Input
                    readOnly={!isEdit}
                    onlyNumbers
                    full
                    placeholder="0.00"
                    fixedAmount
                    hideErrorIcon
                    {...inputProps}
                    {...props}
                />
            )}
        />
    );

    const renderTable = (
        userType: EUserType,
        level: ELoyaltyLevel,
        feePointType: 'fee' | 'point'
    ) => (
        <table
            className={styles.table}
            style={{
                display:
                    activeTab === userType &&
                    activeLevel === level &&
                    activeTabFeePont === feePointType
                        ? 'table'
                        : 'none'
            }}>
            <tbody>
                {paymentMethods.map((item) => (
                    <tr key={item.key}>
                        <td>{item.label}</td>
                        <td>
                            {renderFormField(
                                `${userType}.plans.${level}.${item.key}.${feePointType === 'fee' ? 'flat' : 'points'}`,
                                {
                                    suffix: feePointType === 'fee' ? '$' : '',
                                    prefix: feePointType === 'point' ? 'points' : ''
                                }
                            )}
                        </td>
                        {feePointType === 'point' && <td>per</td>}
                        <td>
                            {renderFormField(
                                `${userType}.plans.${level}.${item.key}.${feePointType === 'fee' ? 'percent' : 'amount'}`,
                                {
                                    suffix: feePointType === 'fee' ? '%' : '',
                                    prefix: feePointType === 'point' ? 'CAD' : ''
                                }
                            )}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    return (
        <div className={styles.wrapper}>
            <FormProvider {...form}>
                <div className={styles.header}>
                    <div className={styles.text_wrapper}>
                        <Typography variant="h4">XEPPT Momentum</Typography>
                    </div>
                </div>
                <div className={styles.actions_content}>
                    <div className={styles.actions_wrapper}>
                        <Button
                            size="normal"
                            onClick={() => setActiveTab(EUserType.INDIVIDUAL)}
                            variant={
                                activeTab === EUserType.INDIVIDUAL ? 'primary' : 'outlined-dark'
                            }>
                            Personal
                        </Button>
                        <Button
                            size="normal"
                            onClick={() => setActiveTab(EUserType.BUSINESS)}
                            variant={
                                activeTab === EUserType.BUSINESS ? 'primary' : 'outlined-dark'
                            }>
                            Business
                        </Button>
                    </div>
                    <div className={styles.actions}>
                        {!isEdit && (
                            <Button
                                size="normal"
                                variant="outlined"
                                leftIcon="edit"
                                onClick={() => setIsEdit(true)}>
                                Edit
                            </Button>
                        )}
                        {isEdit && (
                            <>
                                <Button
                                    size="normal"
                                    variant="outlined-dark"
                                    onClick={() => {
                                        setIsEdit(false);
                                        form.reset(loyalty);
                                    }}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleUpdateSettings}
                                    isLoading={isLoading}
                                    size="normal"
                                    variant="primary">
                                    Save Changes
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                <div className={styles.content}>
                    <div className={styles.block}>
                        <Typography className={styles.title} variant="h5">
                            Profile level settings
                        </Typography>
                        <div className={styles.divider} />
                        <div className={styles.row}>
                            {/* Render all user types but only show the active one */}
                            {userTypes.map((userType) => (
                                <div
                                    key={`profile-${userType}`}
                                    style={{
                                        display: activeTab === userType ? 'contents' : 'none'
                                    }}>
                                    <FormField
                                        name={`${userType}.points_to_${ELoyaltyLevel.B}`}
                                        renderComponent={(props) => (
                                            <Input
                                                readOnly={!isEdit}
                                                onlyNumbers
                                                className={styles.input}
                                                label="Number of points needed for Gold level"
                                                {...props}
                                            />
                                        )}
                                    />
                                    <FormField
                                        name={`${userType}.points_to_${ELoyaltyLevel.C}`}
                                        renderComponent={(props) => (
                                            <Input
                                                readOnly={!isEdit}
                                                onlyNumbers
                                                className={styles.input}
                                                label="Number of points needed for Platinum level"
                                                {...props}
                                            />
                                        )}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={styles.block}>
                        <Typography className={styles.title} variant="h5">
                            Period settings
                        </Typography>
                        <div className={styles.divider} />
                        <div className={styles.row}>
                            {userTypes.map((userType) => (
                                <div
                                    key={`period-${userType}`}
                                    style={{
                                        display: activeTab === userType ? 'contents' : 'none'
                                    }}>
                                    <FormField
                                        name={`${userType}.review_period`}
                                        renderComponent={(props) => (
                                            <Input
                                                readOnly={!isEdit}
                                                onlyNumbers
                                                className={styles.input}
                                                label="Number of days for the period"
                                                {...props}
                                            />
                                        )}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={styles.block}>
                        <Typography className={styles.title} variant="h5">
                            Reminder settings
                        </Typography>
                        <div className={styles.divider} />
                        <div className={styles.row}>
                            {userTypes.map((userType) => (
                                <div
                                    key={`reminder-${userType}`}
                                    style={{
                                        display: activeTab === userType ? 'contents' : 'none'
                                    }}>
                                    <FormField
                                        name={`${userType}.first_remind_in_days`}
                                        renderComponent={(props) => (
                                            <Input
                                                readOnly={!isEdit}
                                                onlyNumbers
                                                className={styles.input}
                                                label="First reminder email (before period ends)"
                                                prefix="days"
                                                {...props}
                                            />
                                        )}
                                    />
                                    <FormField
                                        name={`${userType}.last_remind_in_days`}
                                        renderComponent={(props) => (
                                            <Input
                                                readOnly={!isEdit}
                                                onlyNumbers
                                                className={styles.input}
                                                label="Last reminder email (before period ends)"
                                                prefix="days"
                                                {...props}
                                            />
                                        )}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={styles.block}>
                        <Typography className={styles.title} variant="h5">
                            Invite points
                        </Typography>
                        <div className={styles.divider} />
                        <div className={styles.row}>
                            {userTypes.map((userType) => (
                                <div
                                    key={`profile-${userType}`}
                                    style={{
                                        display: activeTab === userType ? 'contents' : 'none'
                                    }}>
                                    <FormField
                                        name={`${userType}.invite_sender_points`}
                                        renderComponent={(props) => (
                                            <Input
                                                readOnly={!isEdit}
                                                onlyNumbers
                                                className={styles.input}
                                                label="Sender"
                                                prefix="points"
                                                {...props}
                                            />
                                        )}
                                    />
                                    <FormField
                                        name={`${userType}.invite_receiver_points`}
                                        renderComponent={(props) => (
                                            <Input
                                                readOnly={!isEdit}
                                                onlyNumbers
                                                className={styles.input}
                                                label="Receiver"
                                                prefix="points"
                                                {...props}
                                            />
                                        )}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={styles.block}>
                        <Typography className={styles.title} variant="h5">
                            Earning points and fees settings
                        </Typography>
                        <div className={styles.divider} />
                        <div className={styles.actions_wrapper}>
                            <Button
                                size="normal"
                                onClick={() => setActiveLevel(ELoyaltyLevel.A)}
                                variant={
                                    activeLevel === ELoyaltyLevel.A ? 'primary' : 'outlined-dark'
                                }>
                                Silver
                            </Button>
                            <Button
                                size="normal"
                                onClick={() => setActiveLevel(ELoyaltyLevel.B)}
                                variant={
                                    activeLevel === ELoyaltyLevel.B ? 'primary' : 'outlined-dark'
                                }>
                                Gold
                            </Button>
                            <Button
                                size="normal"
                                onClick={() => setActiveLevel(ELoyaltyLevel.C)}
                                variant={
                                    activeLevel === ELoyaltyLevel.C ? 'primary' : 'outlined-dark'
                                }>
                                Platinum
                            </Button>
                        </div>
                        <Tabs
                            //@ts-ignore
                            onChange={(val) => setActiveTabFeePont(val)}
                            tabs={[
                                { value: 'point', label: 'Earning points' },
                                { value: 'fee', label: 'Fees' }
                            ]}
                            className={styles.tabs}
                            activeTab={activeTabFeePont}
                        />

                        {/* Render all possible combinations but only show the active one */}
                        {userTypes.map((userType) =>
                            loyaltyLevels.map((level) =>
                                feePointTypes.map((feePointType) => (
                                    <Fragment key={`${userType}-${level}-${feePointType}`}>
                                        {renderTable(userType, level, feePointType)}
                                    </Fragment>
                                ))
                            )
                        )}
                    </div>
                </div>
            </FormProvider>
        </div>
    );
};

export default LoyaltyLayout;
